import type { Alert, AlertScreenshot } from "@/types/alerts-type";

/** Shared screenshot image pool for dummy alerts (local public assets). */
export const DUMMY_SCREENSHOT_IMAGES = [
  "/sample-1/Shot 9 Smoke source inside UAF (SM-586) (filter 1 of 2).mp4_frame_00000.jpg",
  "/sample-1/Shot 9 Smoke source inside UAF (SM-586) (filter 1 of 2).mp4_frame_00001.jpg",
  "/sample-1/Shot 9 Smoke source inside UAF (SM-586) (filter 1 of 2).mp4_frame_00002.jpg",
  "/sample-1/Shot 9 Smoke source inside UAF (SM-586) (filter 1 of 2).mp4_frame_00003.jpg",
  "/sample-1/Shot 9 Smoke source inside UAF (SM-586) (filter 1 of 2).mp4_frame_00004.jpg",
  "/sample-2/01. FS_Left Side filter.mp4_frame_00000.jpg",
  "/sample-2/01. FS_Left Side filter.mp4_frame_00018.jpg",
  "/sample-2/01. FS_Left Side filter.mp4_frame_00029.jpg",
  "/sample-2/01. FS_Left Side filter.mp4_frame_00034.jpg",
  "/sample-2/01. FS_Left Side filter.mp4_frame_00042.jpg",
  "/sample-3/Shot 5 Outfeed turn table and adjacent conveyor belt.mp4_frame_00001.jpg",
  "/sample-3/Shot 5 Outfeed turn table and adjacent conveyor belt.mp4_frame_00003.jpg",
] as const;

export const DUMMY_VIDEO_FILENAME = "cleanroom_batch_042.mp4";
export const DUMMY_VIDEO_ID = 1;

type MockAlertSeed = {
  id: number;
  type: string;
  zone: string;
  timestamp: string;
  severity: string;
  confidencePercentage: number;
  remark: string;
  status: "Pending" | "Approved" | "Rejected";
  imageIndex: number;
};

/** Same screenshot object shape used on every dummy alert. */
export function createDummyScreenshot(
  alertId: number,
  imageIndex: number,
  remark: string,
): AlertScreenshot {
  const imageUrl =
    DUMMY_SCREENSHOT_IMAGES[imageIndex % DUMMY_SCREENSHOT_IMAGES.length]!;

  return {
    id: `mock-shot-${alertId}`,
    imageUrl,
    remark,
    clipUrl: null,
    clipS3Key: null,
  };
}

const MOCK_ALERT_SEEDS: MockAlertSeed[] = [
  {
    id: 1,
    type: "Non-Laminar",
    zone: "Zone B",
    timestamp: "00:04:32",
    severity: "Critical",
    confidencePercentage: 92,
    remark: "Non-laminar flow detected in Zone B (frame 0)",
    status: "Pending",
    imageIndex: 0,
  },
  {
    id: 2,
    type: "Non-Unidirectional",
    zone: "Zone A",
    timestamp: "00:05:48",
    severity: "Warning",
    confidencePercentage: 78,
    remark: "Non-unidirectional airflow in Zone A (frame 1)",
    status: "Approved",
    imageIndex: 1,
  },
  {
    id: 3,
    type: "Non-Laminar",
    zone: "Zone C",
    timestamp: "00:06:55",
    severity: "Critical",
    confidencePercentage: 88,
    remark: "Turbulent pattern near transfer hatch (frame 2)",
    status: "Pending",
    imageIndex: 2,
  },
  {
    id: 4,
    type: "Non-Unidirectional",
    zone: "Zone B",
    timestamp: "00:08:10",
    severity: "Warning",
    confidencePercentage: 71,
    remark: "Secondary smoke plume detected (frame 3)",
    status: "Pending",
    imageIndex: 3,
  },
  {
    id: 5,
    type: "Non-Laminar",
    zone: "Zone A",
    timestamp: "00:09:24",
    severity: "Critical",
    confidencePercentage: 95,
    remark: "Smoke concentration peak at fill line (frame 4)",
    status: "Rejected",
    imageIndex: 4,
  },
  {
    id: 6,
    type: "Non-Unidirectional",
    zone: "Zone D",
    timestamp: "00:10:41",
    severity: "Warning",
    confidencePercentage: 83,
    remark: "Left-side filter airflow deviation (frame 5)",
    status: "Pending",
    imageIndex: 5,
  },
  {
    id: 7,
    type: "Non-Laminar",
    zone: "Zone C",
    timestamp: "00:11:58",
    severity: "Critical",
    confidencePercentage: 90,
    remark: "Recirculation pocket near vial track (frame 6)",
    status: "Approved",
    imageIndex: 6,
  },
  {
    id: 8,
    type: "Non-Unidirectional",
    zone: "Zone B",
    timestamp: "00:13:12",
    severity: "Warning",
    confidencePercentage: 76,
    remark: "Cross-flow at operator access point (frame 7)",
    status: "Pending",
    imageIndex: 7,
  },
  {
    id: 9,
    type: "Non-Laminar",
    zone: "Zone A",
    timestamp: "00:14:27",
    severity: "Critical",
    confidencePercentage: 87,
    remark: "Smoke retention below HEPA line (frame 8)",
    status: "Pending",
    imageIndex: 8,
  },
  {
    id: 10,
    type: "Non-Unidirectional",
    zone: "Zone D",
    timestamp: "00:15:39",
    severity: "Warning",
    confidencePercentage: 69,
    remark: "Rejected: confirmed lighting artifact (frame 9)",
    status: "Rejected",
    imageIndex: 9,
  },
];

/** Shape used by the mock API adapter (`mockAlerts`). */
export const MOCK_ALERTS_WITH_SCREENSHOTS = MOCK_ALERT_SEEDS.map((seed) => ({
  id: seed.id,
  videoId: DUMMY_VIDEO_ID,
  type: seed.type,
  zone: seed.zone,
  timestamp: seed.timestamp,
  severity: seed.severity,
  confidencePercentage: seed.confidencePercentage,
  screenshot: createDummyScreenshot(seed.id, seed.imageIndex, seed.remark),
  status: seed.status,
}));

/** Shape used directly by `AlertsTable` for local UI testing. */
export const MOCK_ALERT_SCREENSHOT_VIEWER_FIXTURES: Alert[] =
  MOCK_ALERT_SEEDS.map((seed) => ({
    id: String(seed.id),
    videoId: String(DUMMY_VIDEO_ID),
    video: { filename: DUMMY_VIDEO_FILENAME },
    type: seed.type,
    zone: seed.zone,
    timestamp: `2024-01-15T09:${String(30 + seed.id).padStart(2, "0")}:00.000Z`,
    severity: seed.severity,
    confidencePercentage: seed.confidencePercentage,
    status: seed.status.toLowerCase(),
    screenshot: createDummyScreenshot(seed.id, seed.imageIndex, seed.remark),
  }));
